import type { ReactNode } from 'react';
import { BRAND } from '../lib/brand.ts';
import { PoweredByFooter } from './PoweredByFooter.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';

type Variant = 'vendor' | 'admin';

// Per-variant visual DNA. Baked in (not props) because there are exactly two
// consumers — keeping the gradients/pill/corner treatments here stops the two
// login screens from drifting apart the way ad-hoc inline styles would.
const VARIANTS: Record<
  Variant,
  {
    gradient: string;
    pillClass: string;
    // Mobile-only corner treatment on the brand band.
    brandBandClass: string;
    // Mobile-only rounding/overlap on the form panel.
    formPanelClass: string;
  }
> = {
  vendor: {
    gradient: 'linear-gradient(160deg, #4D148C 0%, #3A0F6B 100%)',
    pillClass: 'bg-brand-orange text-white',
    brandBandClass: 'rounded-b-[28px] lg:rounded-none',
    formPanelClass: '',
  },
  admin: {
    gradient: 'linear-gradient(200deg, #2A0B4E 0%, #1A0730 100%)',
    pillClass: 'border border-white/25 bg-white/10 text-white',
    brandBandClass: '',
    // The form card is pulled up over the square-bottomed dark header so its
    // rounded top corners reveal the gradient behind them.
    formPanelClass: '-mt-6 rounded-t-[28px] lg:mt-0 lg:rounded-none',
  },
};

// Default brand-panel copy per variant. The panel is the constant across every
// auth screen (login, reset, invite, expired, 404) — only the form changes — so
// pages can omit these and get the right wording for their side automatically.
const BRAND_PANEL: Record<
  Variant,
  { pill: string; pillCaption: string; headline: string; subtext: string }
> = {
  vendor: {
    pill: 'Vendor',
    pillCaption: 'Compliance portal',
    headline: 'Keep your fleet compliant and ready to deliver',
    subtext:
      'Upload documents, answer questionnaires and track your compliance status — all in one place.',
  },
  admin: {
    pill: 'Admin',
    pillCaption: 'Compliance operations',
    headline: 'Review vendor compliance across your network',
    subtext: 'Restricted access — for authorised compliance administrators only.',
  },
};

function BrandPill({
  label,
  caption,
  pillClass,
  className = '',
}: {
  label: string;
  caption?: string | undefined;
  pillClass: string;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <span
        className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] ${pillClass}`}
      >
        {label}
      </span>
      {caption && <span className="text-sm text-white/80">{caption}</span>}
    </div>
  );
}

/**
 * Split-panel authentication shell: a branded gradient panel beside (desktop)
 * or above (mobile) a white form panel. Both login screens render their form
 * as `children`; everything else is driven by `variant`.
 */
export function AuthSplitLayout({
  variant,
  pill,
  pillCaption,
  headline,
  subtext,
  children,
  formFooter,
}: {
  variant: Variant;
  pill?: string;
  pillCaption?: string;
  headline?: string;
  subtext?: string;
  children: ReactNode;
  formFooter?: ReactNode;
}) {
  const v = VARIANTS[variant];
  const panel = BRAND_PANEL[variant];
  const pillText = pill ?? panel.pill;
  const captionText = pillCaption ?? panel.pillCaption;
  const headlineText = headline ?? panel.headline;
  const subtextText = subtext ?? panel.subtext;
  return (
    <div className="grid min-h-screen grid-rows-[auto_1fr] bg-white lg:grid-cols-[44fr_56fr] lg:grid-rows-1">
      {/* ---- Brand panel ---- */}
      <div
        className={`relative flex flex-col justify-between overflow-hidden px-7 pb-10 pt-16 text-white lg:p-12 ${v.brandBandClass}`}
        style={{ backgroundImage: v.gradient }}
      >
        {/* Decorative circles — ornamental only. */}
        {variant === 'vendor' ? (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full bg-white/5"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -right-14 -top-14 h-64 w-64 rounded-full"
              style={{ background: 'rgba(255,102,0,0.12)' }}
            />
          </>
        ) : (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-28 -right-28 h-80 w-80 rounded-full border border-white/10"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-14 -right-14 h-64 w-64 rounded-full border border-white/10"
            />
          </>
        )}

        {/* Top: logo + "Powered by" eyebrow (+ pill on mobile). */}
        <div className="relative flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <img
              src={BRAND.logoReverseUrl}
              alt={BRAND.logoAlt}
              className="h-8 w-auto self-start lg:h-10"
            />
            <PoweredByFooter tone="onDark" />
          </div>
          <BrandPill
            label={pillText}
            caption={captionText}
            pillClass={v.pillClass}
            className="lg:hidden"
          />
        </div>

        {/* Bottom (desktop only): pill + headline + subtext. */}
        <div className="relative hidden lg:flex lg:flex-col lg:gap-5">
          <BrandPill label={pillText} caption={captionText} pillClass={v.pillClass} />
          <h1 className="max-w-[22rem] text-3xl font-bold leading-tight tracking-tight">
            {headlineText}
          </h1>
          <p className="max-w-[22rem] text-sm leading-relaxed text-white/70">{subtextText}</p>
        </div>
      </div>

      {/* ---- Form panel ---- */}
      <div
        className={`relative z-10 flex flex-col justify-start bg-white px-6 pb-12 pt-9 lg:justify-center lg:px-12 lg:py-12 ${v.formPanelClass}`}
      >
        <div className="mx-auto flex w-full max-w-[380px] flex-col">
          {children}
          {formFooter && (
            <div className="mt-8 text-center text-xs text-slate-400">{formFooter}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The small rounded "info" row used under each login form. `purple` matches the
 * vendor accent; `neutral` (orange dot on a soft grey chip) is the admin note.
 */
export function AuthInfoChip({
  tone,
  children,
}: {
  tone: 'purple' | 'neutral';
  children: ReactNode;
}) {
  const wrap =
    tone === 'purple'
      ? 'bg-[#F3EEFA] text-brand-primary'
      : 'border border-slate-200 bg-slate-50 text-slate-500';
  const dot = tone === 'purple' ? 'bg-brand-primary' : 'bg-brand-orange';
  return (
    <div className={`flex items-center gap-2.5 rounded-lg px-3.5 py-3 ${wrap}`}>
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${dot}`} aria-hidden />
      <p className="text-[12.5px] leading-snug">{children}</p>
    </div>
  );
}

/**
 * A terminal message rendered inside the split shell — used for expired
 * sessions, failed link redemptions and the 404, so those screens share the
 * login look instead of falling back to a bare centred card.
 */
export function AuthNotice({
  variant,
  title,
  children,
  error,
  action,
}: {
  variant: Variant;
  title: string;
  children: ReactNode;
  error?: unknown;
  action?: ReactNode;
}) {
  return (
    <AuthSplitLayout variant={variant}>
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl text-slate-900">{title}</h2>
        <div className="text-sm leading-relaxed text-slate-500">{children}</div>
        {error != null && <ErrorMessage error={error} />}
        {action && <div className="pt-1 text-sm">{action}</div>}
      </div>
    </AuthSplitLayout>
  );
}
